import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  Modal,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Tooltip,
  Collapse,
} from '@mui/material';
import { 
  Add as AddIcon, 
  Refresh as RefreshIcon, 
  Search as SearchIcon,
  TrendingUp as CargoIcon,
  TrendingDown as AbonoIcon,
  AccountBalance as SaldoIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Description as DescriptionIcon,
  SwapHoriz as SwapHorizIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import { useAccesos } from '../contexts/AccesosContext';
import { useSucursal } from '../contexts/SucursalContext';

const numberOrZero = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '0.00';
  const num = Number(value);
  if (!Number.isFinite(num)) return '0.00';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Helper para manejar respuestas del backend
const handleApiResponse = async (response) => {
  // Verificar si la respuesta es exitosa
  if (!response.ok) {
    // Intentar leer como texto para ver el error
    const errorText = await response.text();
    console.error('Error del servidor:', errorText);
    throw new Error(`Error HTTP ${response.status}: ${errorText.substring(0, 100)}`);
  }

  // Verificar que sea JSON válido
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    console.error('Respuesta no es JSON:', text);
    throw new Error('La respuesta del servidor no es JSON válido');
  }

  // Parsear JSON
  const data = await response.json();
  
  // Manejar formato con $values (System.Text.Json con referencias circulares)
  if (data.$values) {
    return data.$values;
  }
  
  return data;
};

// Hook personalizado para búsqueda con debounce
const useDebounce = (callback, delay) => {
  const timeoutRef = React.useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

function GestionAuxiliares() {
  const server = process.env.REACT_APP_API_ROUTE;
  const { user } = useUser();
  const { tienePermiso } = useAccesos();
  const { sucursalSeleccionada } = useSucursal();

  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [tiposAuxiliar, setTiposAuxiliar] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [auxiliarInfo, setAuxiliarInfo] = useState(null);
  const [movimientosAuxiliar, setMovimientosAuxiliar] = useState([]);
  
  // Estados para cuentas contables editables
  const [cuentasContables, setCuentasContables] = useState([]);
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);
  const [contrapartidaSeleccionada, setContrapartidaSeleccionada] = useState(null);
  const [configSugerida, setConfigSugerida] = useState(null);

  // Estados para el modal de visualización PDF
  const [openModalPdf, setOpenModalPdf] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfNombre, setPdfNombre] = useState('');

  // Estados para documentos aplicados (muchos-a-muchos)
  const [modalDocumentosOpen, setModalDocumentosOpen] = useState(false);
  const [tiposDocumentoModal, setTiposDocumentoModal] = useState([]); // Tipos para el modal
  const [tipoDocumentoSeleccionado, setTipoDocumentoSeleccionado] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [documentosPendientes, setDocumentosPendientes] = useState([]);
  const [documentosSeleccionados, setDocumentosSeleccionados] = useState([]);
  const [documentosAplicados, setDocumentosAplicados] = useState([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);

  // Estado para filas expandidas en historial de movimientos
  const [filasExpandidas, setFilasExpandidas] = useState({});
  // Filtros con búsqueda en tiempo real
  const [filtroTipoAuxiliar, setFiltroTipoAuxiliar] = useState(null);
  const [filtroReferencia, setFiltroReferencia] = useState(null);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [opcionesAuxiliares, setOpcionesAuxiliares] = useState([]);
  const [buscandoAuxiliares, setBuscandoAuxiliares] = useState(false);

  const [form, setForm] = useState({
    modo: 'cargo',
    idReferencia: '',
    idTipoAuxiliar: null,
    idSucursal: null,
    idAsientoContable: null,
    descripcion: '',
    monto: '',
    fechaDocumento: new Date().toISOString().split('T')[0],
    idCuentaContable: null,
    idCuentaContrapartida: null,
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Función de búsqueda de auxiliares en tiempo real
  const buscarAuxiliaresEnTiempoReal = async (query) => {
    if (!filtroTipoAuxiliar?.id || !query || query.length < 2) {
      setOpcionesAuxiliares([]);
      return;
    }

    setBuscandoAuxiliares(true);
    try {
      const response = await fetch(
        `${server}/ctb/auxiliares/${filtroTipoAuxiliar.id}/busqueda?query=${encodeURIComponent(query)}`
      );
      
      // Verificar si la respuesta es exitosa
      if (!response.ok) {
        // Intentar leer el error como texto si no es JSON
        const errorText = await response.text();
        console.error('Error del servidor:', errorText);
        setOpcionesAuxiliares([]);
        return;
      }

      // Intentar parsear JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('La respuesta no es JSON válido');
        setOpcionesAuxiliares([]);
        return;
      }

      const data = await response.json();
      
      // Manejar diferentes formatos de respuesta del backend
      let resultados = [];
      if (data.exito && data.data) {
        resultados = data.data.$values || data.data || [];
      } else if (data.$values) {
        resultados = data.$values;
      } else if (Array.isArray(data)) {
        resultados = data;
      }
      
      // Normalizar campos que pueden venir con diferentes nombres del backend
      const resultadosNormalizados = resultados.map(item => {
        // Normalizar codigo (puede venir como codigo, identidad, rtn, Codigo, etc.)
        const codigoNormalizado = item.codigo || item.identidad || item.rtn || item.Codigo || item.numeroDocumento;
        
        // Normalizar id (puede venir como id_empleado, id_cliente, id_proveedor, idEmpleado, etc.)
        // Si no hay campo de ID, usar el codigo como ID (común en empleados)
        const idNormalizado = item.id || item.id_empleado || item.id_cliente || item.id_proveedor || 
                             item.idEmpleado || item.idCliente || item.idProveedor || item.Id || 
                             codigoNormalizado;
        
        return {
          ...item,
          id: idNormalizado,
          codigo: codigoNormalizado,
          // Normalizar descripcion (puede venir como descripcion, nombre_completo, nombreCompleto, nombre, etc.)
          descripcion: item.descripcion || item.nombre_completo || item.nombreCompleto || item.nombre || item.Descripcion || item.NombreCompleto
        };
      });
      
      setOpcionesAuxiliares(resultadosNormalizados);
    } catch (error) {
      console.error('Error al buscar auxiliares:', error);
      setOpcionesAuxiliares([]);
    } finally {
      setBuscandoAuxiliares(false);
    }
  };

  // Debounce para la búsqueda
  const debouncedBusqueda = useDebounce(buscarAuxiliaresEnTiempoReal, 300);

  // Efecto para buscar cuando cambia el texto
  useEffect(() => {
    if (busquedaTexto) {
      debouncedBusqueda(busquedaTexto);
    } else {
      setOpcionesAuxiliares([]);
    }
  }, [busquedaTexto]);

  // Limpiar opciones cuando cambia el tipo de auxiliar
  useEffect(() => {
    setOpcionesAuxiliares([]);
    setFiltroReferencia(null);
    setBusquedaTexto('');
    setAuxiliarInfo(null);
    setMovimientosAuxiliar([]);
  }, [filtroTipoAuxiliar]);

  // Cargar configuración sugerida cuando cambia el tipo de auxiliar en el formulario
  useEffect(() => {
    if (form.idTipoAuxiliar?.id && cuentasContables.length > 0) {
      cargarConfiguracionSugerida(form.idTipoAuxiliar.id);
    } else {
      setConfigSugerida(null);
      setCuentaSeleccionada(null);
      setContrapartidaSeleccionada(null);
    }
  }, [form.idTipoAuxiliar, cuentasContables]);

  // Función para cargar cuentas contables
  const cargarCuentasContables = async () => {
    try {
      console.log('🔄 Cargando cuentas contables desde:', `${server}/CtbPlanDeCuentas/detalle`);
      const response = await fetch(`${server}/CtbPlanDeCuentas/detalle`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) throw new Error('Error al cargar cuentas contables');

      const data = await handleApiResponse(response);
      console.log('✅ Cuentas contables cargadas:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('📄 Ejemplo de cuenta:', data[0]);
      }
      setCuentasContables(data || []);
    } catch (error) {
      console.error('❌ Error al cargar cuentas contables:', error);
      setCuentasContables([]);
    }
  };

  // Función para cargar configuración sugerida del auxiliar
  const cargarConfiguracionSugerida = async (idTipoAuxiliar) => {
    if (!idTipoAuxiliar) return;

    console.log('🔍 Cargando configuración para tipo auxiliar ID:', idTipoAuxiliar);
    console.log('📊 Cuentas contables disponibles:', cuentasContables.length);

    try {
      const response = await fetch(`${server}/ctb/config-auxiliares/config-auxiliar/${idTipoAuxiliar}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        console.warn('⚠️ No se encontró configuración sugerida para este tipo de auxiliar');
        setConfigSugerida(null);
        setCuentaSeleccionada(null);
        setContrapartidaSeleccionada(null);
        return;
      }

      // El backend devuelve {exito: true, data: {...}}
      const result = await response.json();
      const configData = result.data; // Extraer el objeto data
      
      console.log('✅ Configuración recibida:', configData);
      console.log('📋 idCuenta en config:', configData?.idCuenta);
      console.log('📋 idCuentaContrapartida en config:', configData?.idCuentaContrapartida);
      setConfigSugerida(configData);

      // Establecer cuentas sugeridas si existen
      if (configData?.idCuenta) {
        console.log('🔎 Buscando cuenta principal con ID:', configData.idCuenta, 'tipo:', typeof configData.idCuenta);
        console.log('🔎 Primeras 3 cuentas del catálogo:', cuentasContables.slice(0, 3).map(c => ({
          idCuenta: c.idCuenta,
          codigo: c.codigo,
          tipo: typeof c.idCuenta
        })));
        
        const cuentaPrincipal = cuentasContables.find(c => {
          // Comparar como números para evitar problemas de tipos
          const cuentaId = Number(c.idCuenta);
          const configId = Number(configData.idCuenta);
          console.log(`Comparando: ${cuentaId} === ${configId}? ${cuentaId === configId}`);
          return cuentaId === configId;
        });
        
        console.log('✅ Cuenta principal encontrada:', cuentaPrincipal);
        if (cuentaPrincipal) {
          setCuentaSeleccionada(cuentaPrincipal);
          console.log('✅ Cuenta principal establecida en estado');
        } else {
          console.warn('⚠️ No se encontró la cuenta principal en el catálogo');
          console.warn('⚠️ IDs disponibles en catálogo:', cuentasContables.map(c => c.idCuenta));
        }
      }

      if (configData?.idCuentaContrapartida) {
        console.log('🔎 Buscando cuenta contrapartida con ID:', configData.idCuentaContrapartida, 'tipo:', typeof configData.idCuentaContrapartida);
        
        const contrapartida = cuentasContables.find(c => {
          const cuentaId = Number(c.idCuenta);
          const configId = Number(configData.idCuentaContrapartida);
          return cuentaId === configId;
        });
        
        console.log('✅ Cuenta contrapartida encontrada:', contrapartida);
        if (contrapartida) {
          setContrapartidaSeleccionada(contrapartida);
          console.log('✅ Cuenta contrapartida establecida en estado');
        } else {
          console.warn('⚠️ No se encontró la cuenta contrapartida en el catálogo');
        }
      }
    } catch (error) {
      console.error('❌ Error al cargar configuración sugerida:', error);
      setConfigSugerida(null);
      setCuentaSeleccionada(null);
      setContrapartidaSeleccionada(null);
    }
  };

  const cargarCatalogos = async () => {
    try {
      const [auxRes, docRes, sucRes] = await Promise.all([
        fetch(`${server}/CtbTipoAuxiliars`),
        fetch(`${server}/ctb/tipo-documentos/all`).catch(() => null),
        fetch(`${server}/MgSucursales`).catch(() => null),
      ]);

      // Tipos de auxiliar
      if (auxRes.ok) {
        const auxJson = await auxRes.json();
        setTiposAuxiliar(auxJson.$values || auxJson || []);
      } else {
        console.error('Error al cargar tipos de auxiliar');
        setTiposAuxiliar([]);
      }

      // Tipos de documento - cargar todos y filtrar solo activos
      if (docRes && docRes.ok) {
        const contentType = docRes.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const docJson = await docRes.json();
          const docData = docJson.$values || docJson || [];
          // Filtrar solo los activos
          const documentosActivos = docData.filter(doc => doc.activo === true);
          setTiposDocumento(documentosActivos);
        } else {
          console.warn('Respuesta de tipo documento no es JSON');
          setTiposDocumento([]);
        }
      } else {
        console.warn('Endpoint tipo-documentos no disponible o retornó error');
        setTiposDocumento([]);
      }

      // Sucursales
      if (sucRes && sucRes.ok) {
        const sucJson = await sucRes.json();
        const sucData = sucJson.$values || sucJson || [];
        setSucursales(sucData);
        
        // Log del contexto completo para debugging
        console.log('🔍 Sucursal del contexto:', sucursalSeleccionada);
        console.log('🔍 Sucursales disponibles:', sucData);
        
        // Seleccionar la sucursal actualmente seleccionada en el contexto
        if (sucursalSeleccionada?.idSucursal) {
          // Buscar por diferentes posibles campos ID
          const sucursalActual = sucData.find(s => 
            s.id === sucursalSeleccionada.idSucursal || 
            s.Id === sucursalSeleccionada.idSucursal ||
            s.idSucursal === sucursalSeleccionada.idSucursal
          );
          
          if (sucursalActual) {
            console.log('✅ Sucursal del contexto cargada:', sucursalActual);
            setForm(prev => ({ ...prev, idSucursal: sucursalActual }));
          } else {
            console.warn('⚠️ No se encontró la sucursal del contexto con ID:', sucursalSeleccionada.idSucursal);
            // Intentar con la primera sucursal como fallback
            if (sucData.length > 0) {
              console.log('ℹ️ Usando primera sucursal disponible como fallback');
              setForm(prev => ({ ...prev, idSucursal: sucData[0] }));
            }
          }
        } else {
          console.warn('⚠️ No hay sucursal seleccionada en el contexto');
          // Usar la primera sucursal disponible como fallback
          if (sucData.length > 0) {
            console.log('ℹ️ Usando primera sucursal disponible');
            setForm(prev => ({ ...prev, idSucursal: sucData[0] }));
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
      Swal.fire('Error', 'No se pudieron cargar los catálogos.', 'error');
    }
  };

  const buscarAuxiliar = async () => {
    if (!filtroTipoAuxiliar?.id || !filtroReferencia?.id) {
      Swal.fire('Validación', 'Seleccione tipo de auxiliar y referencia.', 'warning');
      return;
    }

    try {
      setLoadingInfo(true);
      setAuxiliarInfo(null);
      setMovimientosAuxiliar([]);

      // Obtener información del auxiliar (codificar ID por si es alfanumérico con _ o -)
      const infoRes = await fetch(
        `${server}/ctb/movimientos-auxiliares/info/${filtroTipoAuxiliar.id}/${encodeURIComponent(filtroReferencia.id)}`
      );
      
      if (!infoRes.ok) {
        const errorText = await infoRes.text();
        console.warn('⚠️ No se encontró información previa del auxiliar:', errorText);
        
        // Si no existe información previa, crear una estructura básica para permitir ingreso de saldo inicial
        setAuxiliarInfo({
          idTipoAuxiliar: filtroTipoAuxiliar.id,
          idReferencia: filtroReferencia.id,
          codigo: filtroReferencia.codigo || filtroReferencia.id.toString(),
          descripcion: filtroReferencia.descripcion || 'Auxiliar sin movimientos previos',
          esNuevo: true, // Marcador para indicar que es el primer registro
        });
        
        // Prellenar el formulario
        setForm(prev => ({
          ...prev,
          idReferencia: filtroReferencia.id,
          idTipoAuxiliar: filtroTipoAuxiliar,
        }));
        
        setLoadingInfo(false);
        
        Swal.fire({
          icon: 'info',
          title: 'Nuevo Auxiliar',
          text: 'Este auxiliar no tiene movimientos previos. Puede ingresar el saldo inicial o primer movimiento.',
          confirmButtonText: 'Continuar'
        });
        
        return; // Salir sin error
      }

      const contentType = infoRes.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('La respuesta del servidor no es JSON válido');
      }

      const infoData = await infoRes.json();
      
      if (!infoData.exito) {
        throw new Error(infoData.mensaje || 'Error al obtener información');
      }

      setAuxiliarInfo(infoData.data);

      // Obtener movimientos
      const movRes = await fetch(
        `${server}/ctb/movimientos-auxiliares/${filtroTipoAuxiliar.id}/${filtroReferencia.id}`
      );
      
      if (movRes.ok) {
        const movContentType = movRes.headers.get('content-type');
        if (movContentType && movContentType.includes('application/json')) {
          const movData = await movRes.json();
          if (movData.exito && movData.data?.movimientos) {
            const movimientos = movData.data.movimientos.$values || movData.data.movimientos || [];
            setMovimientosAuxiliar(movimientos);
          }
        }
      }

      // Prellenar el formulario
      setForm(prev => ({
        ...prev,
        idReferencia: filtroReferencia.id,
        idTipoAuxiliar: filtroTipoAuxiliar,
      }));

    } catch (error) {
      console.error('Error al buscar auxiliar:', error);
      Swal.fire('Error', error.message || 'No se pudo obtener la información del auxiliar.', 'error');
    } finally {
      setLoadingInfo(false);
    }
  };

  useEffect(() => {
    cargarCatalogos();
    cargarCuentasContables();
  }, []);

  // Limpiar URL del PDF al cerrar modal
  useEffect(() => {
    if (!openModalPdf && pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
  }, [openModalPdf]);

  const resetForm = () => {
    const sucursalActual = form.idSucursal;
    const asientoActual = form.idAsientoContable;
    setForm({
      modo: 'cargo',
      idReferencia: filtroReferencia?.id || '',
      idTipoAuxiliar: filtroTipoAuxiliar,
      idSucursal: sucursalActual,
      idAsientoContable: asientoActual,
      descripcion: '',
      monto: '',
      fechaDocumento: new Date().toISOString().split('T')[0],
      idCuentaContable: null,
      idCuentaContrapartida: null,
    });
    setDocumentosAplicados([]);
    setCuentaSeleccionada(null);
    setContrapartidaSeleccionada(null);
    setConfigSugerida(null);
  };

  // Función para imprimir partida
  const imprimirPartida = async (idPartidaContable) => {
    if (!idPartidaContable) {
      Swal.fire('Error', 'No se encontró el ID de la partida contable.', 'warning');
      return;
    }

    try {
      const response = await fetch(`${server}/InformesContablesLinux/asiento/${idPartidaContable}/ReportAsiento`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        }
      });
      
      if (!response.ok) {
        throw new Error('Error al generar el PDF');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      
      setPdfUrl(url);
      setPdfNombre(`Partida #${idPartidaContable}`);
      setOpenModalPdf(true);
    } catch (error) {
      console.error('Error al imprimir partida:', error);
      Swal.fire('Error', 'No se pudo generar el PDF de la partida.', 'error');
    }
  };

  // Cerrar modal PDF
  const handleCloseModalPdf = () => {
    setOpenModalPdf(false);
  };

  // Descargar PDF
  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `${pdfNombre}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ==================== FUNCIONES MODIFICAR/ELIMINAR/DESAPLICAR ====================

  // Cambiar tipo de movimiento (cargo ↔ abono)
  const handleCambiarTipo = async (mov) => {
    const eraCargo = (mov.cargo || 0) > 0;
    const result = await Swal.fire({
      title: 'Cambiar Tipo de Movimiento',
      html: `¿Desea cambiar este movimiento de <strong>${eraCargo ? 'CARGO' : 'ABONO'}</strong> a <strong>${eraCargo ? 'ABONO' : 'CARGO'}</strong>?<br/><br/>Monto: <strong>L. ${formatCurrency(eraCargo ? mov.cargo : mov.abono)}</strong><br/><small>Se actualizará la partida contable y se recalcularán los saldos.</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, cambiar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1976d2',
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const usuarioFormateado = (user?.codigoUsuario || 'SYSTEM').padEnd(20, ' ').substring(0, 20);
      const response = await fetch(`${server}/ctb/movimientos-auxiliares/${mov.id}/cambiar-tipo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modificadoPor: usuarioFormateado }),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok || data?.exito === false) {
        throw new Error(data?.mensaje || `Error HTTP ${response.status}`);
      }

      Swal.fire('Éxito', data?.mensaje || 'Tipo de movimiento cambiado exitosamente.', 'success');
      await buscarAuxiliar();
    } catch (error) {
      Swal.fire('Error', error.message || 'No se pudo cambiar el tipo de movimiento.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Eliminar movimiento auxiliar
  const handleEliminarMovimiento = async (mov) => {
    const esCargo = (mov.cargo || 0) > 0;
    const result = await Swal.fire({
      title: 'Eliminar Movimiento',
      html: `¿Está seguro de eliminar este movimiento?<br/><br/>${esCargo ? 'Cargo' : 'Abono'}: <strong>L. ${formatCurrency(esCargo ? mov.cargo : mov.abono)}</strong><br/>Fecha: ${mov.fechaDocumento || '-'}<br/><br/><small>Se eliminará la partida contable asociada y se recalcularán los saldos.</small>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d32f2f',
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const usuarioFormateado = (user?.codigoUsuario || 'SYSTEM').padEnd(20, ' ').substring(0, 20);
      const response = await fetch(
        `${server}/ctb/movimientos-auxiliares/${mov.id}?modificadoPor=${encodeURIComponent(usuarioFormateado)}`,
        { method: 'DELETE' }
      );

      const data = await response.json().catch(() => null);
      if (!response.ok || data?.exito === false) {
        throw new Error(data?.mensaje || `Error HTTP ${response.status}`);
      }

      Swal.fire('Éxito', data?.mensaje || 'Movimiento eliminado exitosamente.', 'success');
      await buscarAuxiliar();
    } catch (error) {
      Swal.fire('Error', error.message || 'No se pudo eliminar el movimiento.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Exportar movimientos a Excel
  const exportarAExcel = () => {
    if (!auxiliarInfo || movimientosAuxiliar.length === 0) {
      Swal.fire('Validación', 'No hay movimientos para exportar.', 'warning');
      return;
    }

    try {
      // Crear workbook
      const wb = XLSX.utils.book_new();

      // Hoja de Resumen
      const infoWs = XLSX.utils.aoa_to_sheet([
        ['AUXILIAR CONTABLE'],
        [''],
        ['Código:', auxiliarInfo.codigo || ''],
        ['Descripción:', auxiliarInfo.descripcion || ''],
        ['Saldo Actual:', auxiliarInfo.saldoActual ? `L. ${formatCurrency(auxiliarInfo.saldoActual)}` : 'L. 0.00'],
        ['Fecha Reporte:', new Date().toLocaleDateString('es-ES')],
        [''],
        ['Movimientos Registrados:'],
      ]);
      
      infoWs['!cols'] = [{ wch: 20 }, { wch: 40 }];
      XLSX.utils.book_append_sheet(wb, infoWs, 'Resumen');

      // Hoja de Movimientos
      const datosExportar = movimientosAuxiliar.map((mov, idx) => ({
        '#': idx + 1,
        'Fecha': mov.fechaDocumento || mov.fechaIngreso || '',
        'Tipo Documento': mov.tipoDocumento || '',
        'Cargo': mov.cargo > 0 ? mov.cargo : '',
        'Abono': mov.abono > 0 ? mov.abono : '',
        'Saldo': mov.saldo,
        'Partida': mov.numeroPartida ? `#${mov.numeroPartida}` : '',
      }));

      const ws = XLSX.utils.json_to_sheet(datosExportar);

      // Configurar ancho de columnas
      ws['!cols'] = [
        { wch: 5 },   // #
        { wch: 12 },  // Fecha
        { wch: 18 },  // Tipo Documento
        { wch: 15 },  // Cargo
        { wch: 15 },  // Abono
        { wch: 15 },  // Saldo
        { wch: 12 },  // Partida
      ];

      // Formatear números
      Object.keys(ws).forEach(cell => {
        if (cell.startsWith('D') || cell.startsWith('E') || cell.startsWith('F')) {
          if (ws[cell]?.v && typeof ws[cell].v === 'number') {
            ws[cell].z = '#,##0.00';
          }
        }
      });

      XLSX.utils.book_append_sheet(wb, ws, 'Movimientos');

      // Generar nombre del archivo
      const fecha = new Date().toISOString().split('T')[0];
      const nombreArchivo = `Auxiliar_${auxiliarInfo.codigo}_${fecha}.xlsx`;

      // Descargar
      XLSX.writeFile(wb, nombreArchivo);
      
      Swal.fire('Éxito', 'Archivo exportado correctamente.', 'success');
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      Swal.fire('Error', 'No se pudo exportar el archivo.', 'error');
    }
  };

  // ==================== FUNCIONES DOCUMENTOS APLICADOS ==
  
  // Cargar tipos de documentos
  const cargarTiposDocumentos = async () => {
    try {
      const response = await fetch(`${server}/ctb/documentos-pendientes/tipos`);
      if (response.ok) {
        const tipos = await response.json();
        // Manejar respuesta con $values o array directo
        const tiposArray = tipos.$values || tipos || [];
        setTiposDocumentoModal(tiposArray);
      }
    } catch (error) {
      console.error('Error al cargar tipos de documentos:', error);
      setTiposDocumentoModal([]);
    }
  };

  // Buscar documentos pendientes
  const buscarDocumentosPendientes = async () => {
    if (!tipoDocumentoSeleccionado) {
      Swal.fire('Validación', 'Seleccione un tipo de documento.', 'warning');
      return;
    }

    if (!filtroTipoAuxiliar?.id || !filtroReferencia?.id) {
      Swal.fire('Validación', 'Primero debe seleccionar el tipo de auxiliar y la referencia.', 'warning');
      return;
    }

    setLoadingDocumentos(true);
    try {
      const params = new URLSearchParams();
      if (fechaDesde) params.append('fechaDesde', fechaDesde);
      if (fechaHasta) params.append('fechaHasta', fechaHasta);

      const url = `${server}/ctb/documentos-pendientes/${filtroTipoAuxiliar.id}/${encodeURIComponent(filtroReferencia.id)}/${tipoDocumentoSeleccionado}?${params}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Error al buscar documentos');
      }

      const data = await response.json();
      
      // Manejar respuesta con $values o array directo
      const documentos = data.$values || data || [];
      
      // IDs de documentos ya aplicados en esta transacción (sesión actual)
      const idsYaAplicadosSesion = documentosAplicados.map(d => d.idDocumentoReferencia);
      
      // Excluir solo los docs ya aplicados en esta sesión
      const documentosFiltrados = documentos.filter(doc => 
        !idsYaAplicadosSesion.includes(doc.idDocumento)
      );
      
      // Inicializar montoAplicar con saldoPendiente (redondeado a 2 decimales)
      const documentosConMonto = documentosFiltrados.map(doc => ({
        ...doc,
        montoTotal: Math.round((doc.montoTotal || 0) * 100) / 100,
        montoAplicado: Math.round((doc.montoAplicado || 0) * 100) / 100,
        saldoPendiente: Math.round((doc.saldoPendiente || 0) * 100) / 100,
        montoAplicar: Math.round((doc.saldoPendiente || 0) * 100) / 100
      }));
      
      setDocumentosPendientes(documentosConMonto);
    } catch (error) {
      console.error('Error al buscar documentos:', error);
      Swal.fire('Error', 'No se pudieron cargar los documentos pendientes.', 'error');
    } finally {
      setLoadingDocumentos(false);
    }
  };

  // Actualizar monto a aplicar en la búsqueda
  const actualizarMontoAplicar = (idDocumento, nuevoMonto) => {
    const montoRedondeado = Math.round((nuevoMonto || 0) * 100) / 100;
    const updatedDocs = documentosPendientes.map(doc =>
      doc.idDocumento === idDocumento
        ? { ...doc, montoAplicar: montoRedondeado }
        : doc
    );
    setDocumentosPendientes(updatedDocs);
  };

  // Calcular total seleccionado
  const calcularTotalSeleccionado = () => {
    return documentosSeleccionados.reduce((sum, doc) => {
      const documento = documentosPendientes.find(d => d.idDocumento === doc);
      return sum + (documento?.montoAplicar || 0);
    }, 0);
  };

  // Aplicar documentos seleccionados
  const aplicarDocumentosSeleccionados = () => {
    if (documentosSeleccionados.length === 0) {
      Swal.fire('Validación', 'Debe seleccionar al menos un documento.', 'warning');
      return;
    }

    const nuevosDocumentos = documentosPendientes
      .filter(doc => documentosSeleccionados.includes(doc.idDocumento))
      .map(doc => ({
        tipoDocumentoReferencia: doc.tipoDocumento,
        idDocumentoReferencia: doc.idDocumento,
        numeroDocumento: doc.numeroDocumento,
        montoDocumento: Math.round((doc.montoTotal || 0) * 100) / 100,
        montoAplicado: Math.round((doc.montoAplicar || 0) * 100) / 100,
        saldoPendiente: Math.round(((doc.saldoPendiente || 0) - (doc.montoAplicar || 0)) * 100) / 100,
        fechaDocumento: doc.fechaDocumento
      }));

    // Validar que todos los documentos (existentes + nuevos) sean del mismo tipo
    const todosDocumentos = [...documentosAplicados, ...nuevosDocumentos];
    const tipos = [...new Set(todosDocumentos.map(d => d.tipoDocumentoReferencia))];
    if (tipos.length > 1) {
      Swal.fire('Validación', 'Solo puede aplicar documentos de un mismo tipo a la vez. Elimine los documentos existentes si desea cambiar el tipo.', 'warning');
      return;
    }

    setDocumentosAplicados([...documentosAplicados, ...nuevosDocumentos]);
    setModalDocumentosOpen(false);
    setDocumentosSeleccionados([]);
    setDocumentosPendientes([]);
    setTipoDocumentoSeleccionado('');
    setFechaDesde('');
    setFechaHasta('');

    Swal.fire('Éxito', `${nuevosDocumentos.length} documento(s) agregado(s).`, 'success');
  };

  // Editar monto aplicado en tabla principal
  const editarMontoAplicado = (idDocumentoReferencia, nuevoMonto) => {
    const montoRedondeado = Math.round((nuevoMonto || 0) * 100) / 100;
    const updatedDocs = documentosAplicados.map(doc =>
      doc.idDocumentoReferencia === idDocumentoReferencia
        ? { ...doc, montoAplicado: montoRedondeado }
        : doc
    );
    setDocumentosAplicados(updatedDocs);
  };

  // Eliminar documento aplicado
  const eliminarDocumento = (idDocumentoReferencia) => {
    const filtered = documentosAplicados.filter(
      doc => doc.idDocumentoReferencia !== idDocumentoReferencia
    );
    setDocumentosAplicados(filtered);
  };

  // Calcular total aplicado
  const calcularTotalAplicado = () => {
    return documentosAplicados.reduce((sum, doc) => sum + doc.montoAplicado, 0);
  };

  // Validar total aplicado
  const validarTotalAplicado = () => {
    const cargo = numberOrZero(form.monto);
    const totalAplicado = calcularTotalAplicado();
    return Math.abs(totalAplicado - cargo) < 0.01;
  };

  // Validar formulario con documentos
  const validarFormularioConDocumentos = () => {
    if (documentosAplicados.length === 0) {
      return 'Debe agregar al menos un documento aplicado.';
    }

    if (!validarTotalAplicado()) {
      const cargo = numberOrZero(form.monto);
      const totalAplicado = calcularTotalAplicado();
      return `El total aplicado (L.${formatCurrency(totalAplicado)}) debe ser igual al ${form.modo} (L.${formatCurrency(cargo)}).`;
    }

    for (const doc of documentosAplicados) {
      if (doc.montoAplicado <= 0) {
        return `El monto aplicado del documento ${doc.numeroDocumento} debe ser mayor a cero.`;
      }
      if (doc.montoAplicado > doc.montoDocumento) {
        return `El monto aplicado del documento ${doc.numeroDocumento} no puede ser mayor al total del documento.`;
      }
    }

    return null;
  };

  // Abrir modal de documentos
  const abrirModalDocumentos = () => {
    if (!filtroTipoAuxiliar || !filtroReferencia) {
      Swal.fire('Validación', 'Primero debe seleccionar el tipo de auxiliar y la referencia.', 'warning');
      return;
    }
    setModalDocumentosOpen(true);
  };

  // Cargar tipos de documentos al cargar catálogos
  useEffect(() => {
    if (tiposDocumento.length === 0) {
      cargarTiposDocumentos();
    }
  }, []);

  // =======================================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    const { 
      modo, 
      idReferencia, 
      idTipoAuxiliar, 
      idSucursal,
      descripcion, 
      monto, 
      fechaDocumento 
    } = form;

    // Validaciones
    if (!idReferencia) {
      Swal.fire('Validación', 'Seleccione una referencia válida.', 'warning');
      return;
    }
    if (!idTipoAuxiliar?.id) {
      Swal.fire('Validación', 'Seleccione un tipo de auxiliar.', 'warning');
      return;
    }
    if (!idSucursal || (!idSucursal.id && !idSucursal.idSucursal)) {
      Swal.fire('Validación', 'La sucursal no está configurada. Contacte al administrador.', 'warning');
      return;
    }
    if (!descripcion.trim()) {
      Swal.fire('Validación', 'La descripción es obligatoria.', 'warning');
      return;
    }
    if (!monto || numberOrZero(monto) <= 0) {
      Swal.fire('Validación', 'El monto debe ser mayor a cero.', 'warning');
      return;
    }
    if (documentosAplicados.length === 0) {
      Swal.fire('Validación', 'Debe agregar al menos un documento aplicado para determinar el tipo de documento.', 'warning');
      return;
    }

    // Validación específica para abono
    if (modo === 'abono') {
      const saldoActual = movimientosAuxiliar.length > 0 
        ? movimientosAuxiliar[movimientosAuxiliar.length - 1].saldo 
        : 0;
      
      if (numberOrZero(monto) > saldoActual) {
        Swal.fire('Validación', `El abono (L. ${formatCurrency(monto)}) no puede ser mayor al saldo actual (L. ${formatCurrency(saldoActual)}).`, 'warning');
        return;
      }
    }

    // Validar documentos aplicados si hay documentos
    if (documentosAplicados.length > 0) {
      const errorDocumentos = validarFormularioConDocumentos();
      if (errorDocumentos) {
        Swal.fire('Validación', errorDocumentos, 'warning');
        return;
      }
    }

    const esCargo = modo === 'cargo';
    
    // Formatear usuario a 20 caracteres
    const usuarioFormateado = (user?.codigoUsuario || 'SYSTEM').padEnd(20, ' ').substring(0, 20);
    
    // Determinar si es empleado (tipo auxiliar 4) para usar código alfanumérico
    const esEmpleado = idTipoAuxiliar.id === 4;
    
    const payload = {
      // Para empleados usar CodigoReferencia (string), para otros usar IdReferencia (int)
      ...(esEmpleado 
        ? { codigoReferencia: idReferencia } // idReferencia contiene el código alfanumérico
        : { idReferencia: numberOrZero(idReferencia) }
      ),
      idTipoAuxiliar: idTipoAuxiliar.id,
      idTipoDocumento: documentosAplicados[0].tipoDocumentoReferencia,
      descripcion: descripcion.trim(),
      [esCargo ? 'cargo' : 'abono']: numberOrZero(monto),
      fechaDocumento,
      idSucursal: idSucursal.idSucursal || idSucursal.id,
      ingresadoPor: usuarioFormateado,
      // Agregar cuentas contables seleccionadas
      idCuentaContable: cuentaSeleccionada?.id || cuentaSeleccionada?.idCuenta || null,
      idCuentaContrapartida: contrapartidaSeleccionada?.id || contrapartidaSeleccionada?.idCuenta || null,
      // Agregar documentos aplicados si existen
      ...(documentosAplicados.length > 0 && { documentosAplicados }),
    };

    console.log('📤 Payload enviado:', payload);

    try {
      setLoading(true);
      
      // Usar endpoint diferente si hay documentos aplicados
      const endpoint = documentosAplicados.length > 0
        ? `${server}/ctb/movimientos-auxiliares/${esCargo ? 'cargo' : 'abono'}-con-detalles`
        : `${server}/ctb/movimientos-auxiliares/${esCargo ? 'cargo' : 'abono'}`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Verificar si la respuesta es JSON válido
      const contentType = response.headers.get('content-type');
      let result = null;
      
      if (contentType && contentType.includes('application/json')) {
        result = await response.json().catch(() => null);
      } else {
        // Si no es JSON, leer como texto para mostrar el error
        const errorText = await response.text();
        console.error('Error del servidor:', errorText);
      }

      if (!response.ok || result?.exito === false) {
        const message = result?.mensaje || `Error HTTP ${response.status}`;
        throw new Error(message);
      }

      // Mostrar mensaje con número de partida generada
      const numeroPartida = result?.data?.numeroPartida || '';
      const mensaje = `${result?.mensaje || 'Movimiento auxiliar registrado.'}\n${numeroPartida ? `Partida #${numeroPartida} generada automáticamente.` : ''}`;
      
      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: mensaje,
        confirmButtonText: 'Aceptar'
      });

      resetForm();
      // Limpiar documentos aplicados
      setDocumentosAplicados([]);
      // Recargar movimientos
      await buscarAuxiliar();
    } catch (error) {
      console.error('Error al registrar auxiliar:', error);
      Swal.fire('Error', error.message || 'No se pudo registrar el auxiliar.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const totales = useMemo(() => {
    const cargos = movimientosAuxiliar.reduce((sum, mov) => sum + (mov.cargo || 0), 0);
    const abonos = movimientosAuxiliar.reduce((sum, mov) => sum + (mov.abono || 0), 0);
    const saldo = movimientosAuxiliar.length > 0 
      ? movimientosAuxiliar[movimientosAuxiliar.length - 1].saldo 
      : 0;
    return { cargos, abonos, saldo };
  }, [movimientosAuxiliar]);

  return (
    <div className="right_col" role="main">
      <div className="">
        <div className="page-title">
          <div className="title_left">
            <h3>🔍 Gestión de Auxiliares Contables</h3>
          </div>
        </div>
        <div className="clearfix"></div>

        <Grid container spacing={3}>
          {/* Sección de Filtros */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  📊 Filtros de Búsqueda
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={tiposAuxiliar}
                      getOptionLabel={(option) => option.tipoAuxiliar || option.TipoAuxiliar || ''}
                      value={filtroTipoAuxiliar}
                      onChange={(e, newValue) => setFiltroTipoAuxiliar(newValue)}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField {...params} label="Tipo Auxiliar *" size="small" fullWidth />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Autocomplete
                      options={opcionesAuxiliares}
                      getOptionLabel={(option) => option.descripcion || ''}
                      value={filtroReferencia}
                      onChange={(e, newValue) => {
                        setFiltroReferencia(newValue);
                        // No llamar automáticamente, dejar que el usuario decida cuando buscar
                      }}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      inputValue={busquedaTexto}
                      onInputChange={(e, newValue) => {
                        setBusquedaTexto(newValue);
                      }}
                      loading={buscandoAuxiliares}
                      disabled={!filtroTipoAuxiliar}
                      noOptionsText={
                        busquedaTexto.length < 2 
                          ? "Escriba al menos 2 caracteres para buscar" 
                          : buscandoAuxiliares 
                          ? "Buscando..." 
                          : "No se encontraron resultados"
                      }
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Buscar Referencia (código, nombre, identidad) *" 
                          size="small" 
                          fullWidth 
                          placeholder="Escriba al menos 2 caracteres..."
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {buscandoAuxiliares ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}                      renderOption={(props, option) => {
                        const { key, ...otherProps } = props;
                        return (
                          <li key={key || option.id} {...otherProps}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                              <Typography variant="body1" fontWeight="bold">
                                {option.codigo}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {option.descripcion}
                              </Typography>
                            </Box>
                          </li>
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      startIcon={<SearchIcon />}
                      onClick={buscarAuxiliar}
                      disabled={!filtroTipoAuxiliar || !filtroReferencia || loadingInfo}
                    >
                      {loadingInfo ? 'Buscando...' : 'Buscar'}
                    </Button>
                  </Grid>
                </Grid>
                {filtroTipoAuxiliar && !filtroReferencia && busquedaTexto.length < 2 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    💡 Escriba al menos 2 caracteres en el campo de búsqueda para buscar {filtroTipoAuxiliar.tipoAuxiliar?.toLowerCase()}s
                  </Alert>
                )}
                {filtroTipoAuxiliar && filtroReferencia && !auxiliarInfo && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    ℹ️ Haga clic en "Buscar" para cargar la información y movimientos del auxiliar
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Limpiar y Recargar - ahora separados */}
          {auxiliarInfo && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    setFiltroTipoAuxiliar(null);
                    setFiltroReferencia(null);
                    setBusquedaTexto('');
                    setOpcionesAuxiliares([]);
                    setAuxiliarInfo(null);
                    setMovimientosAuxiliar([]);
                  }}
                >
                  Limpiar Búsqueda
                </Button>
              </Box>
            </Grid>
          )}

          {/* Información del Auxiliar */}
          {auxiliarInfo && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ bgcolor: '#e3f2fd' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    📋 Información del Auxiliar
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2" color="textSecondary">Código:</Typography>
                      <Typography variant="h6">{auxiliarInfo.codigo || filtroReferencia?.codigo || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="textSecondary">Nombre/Descripción:</Typography>
                      <Typography variant="h6">{auxiliarInfo.descripcion || filtroReferencia?.descripcion || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SaldoIcon color="primary" />
                        <Box>
                          <Typography variant="body2" color="textSecondary">Saldo Actual:</Typography>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              color: totales.saldo === 0 ? 'success.main' : 'primary.main',
                              fontWeight: 'bold'
                            }}
                          >
                            L. {formatCurrency(totales.saldo)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  {totales.saldo === 0 && movimientosAuxiliar.length > 0 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      ✅ La cuenta está saldada (sin pendientes)
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Formulario de Nuevo Movimiento */}
          {auxiliarInfo && (
            <Grid item xs={12} lg={5}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    ➕ Nuevo Movimiento Auxiliar
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <RadioGroup
                          row
                          value={form.modo}
                          onChange={(e) => setField('modo', e.target.value)}
                        >
                          <FormControlLabel 
                            value="cargo" 
                            control={<Radio />} 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CargoIcon fontSize="small" color="error" />
                                <span>Cargo (Débito)</span>
                              </Box>
                            }
                          />
                          <FormControlLabel 
                            value="abono" 
                            control={<Radio />} 
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <AbonoIcon fontSize="small" color="success" />
                                <span>Abono (Crédito)</span>
                              </Box>
                            }
                          />
                        </RadioGroup>
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Descripción *"
                          value={form.descripcion}
                          onChange={(e) => setField('descripcion', e.target.value)}
                          multiline
                          minRows={2}
                          placeholder="Ej: Factura por venta de servicios..."
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Monto *"
                          value={form.monto}
                          onChange={(e) => setField('monto', e.target.value.replace(/[^0-9.]/g, ''))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">L.</InputAdornment>,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          size="small"
                          type="date"
                          label="Fecha"
                          InputLabelProps={{ shrink: true }}
                          value={form.fechaDocumento}
                          onChange={(e) => setField('fechaDocumento', e.target.value)}
                        />
                      </Grid>

                      {/* Sección de Cuentas Contables Editables */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                          💼 Cuentas Contables
                        </Typography>
                        {configSugerida && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>💡 Sugerencia de Configuración:</strong><br/>
                            Cuenta Principal: <strong>{configSugerida.codigoCuenta} - {configSugerida.nombreCuenta}</strong><br/>
                            {configSugerida.cuentaContrapartida && (
                              <>Contrapartida: <strong>{configSugerida.cuentaContrapartida}</strong></>
                            )}
                            <br/>
                            <small>Puede modificar las cuentas según el caso específico.</small>
                          </Alert>
                        )}
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={cuentasContables}
                              value={cuentaSeleccionada}
                              onChange={(e, newValue) => setCuentaSeleccionada(newValue)}
                              getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                              isOptionEqualToValue={(option, value) => 
                                (option.id || option.idCuenta) === (value.id || value.idCuenta)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Cuenta Principal *"
                                  size="small"
                                  fullWidth
                                  helperText="Cuenta de cargo/abono según el movimiento"
                                />
                              )}
                              disabled={loading}
                            />
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={cuentasContables}
                              value={contrapartidaSeleccionada}
                              onChange={(e, newValue) => setContrapartidaSeleccionada(newValue)}
                              getOptionLabel={(option) => `${option.codigo} - ${option.descripcion}`}
                              isOptionEqualToValue={(option, value) => 
                                (option.id || option.idCuenta) === (value.id || value.idCuenta)
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Cuenta Contrapartida *"
                                  size="small"
                                  fullWidth
                                  helperText="Cuenta de contrapartida en el asiento"
                                />
                              )}
                              disabled={loading}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Sección de Documentos Aplicados */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ReceiptIcon />
                            Documentos Aplicados
                            {documentosAplicados.length > 0 && (
                              <Chip label={documentosAplicados.length} size="small" color="primary" />
                            )}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={abrirModalDocumentos}
                            disabled={!tienePermiso('Buscar Documentos Auxiliar')}
                          >
                            Buscar Documentos
                          </Button>
                        </Box>

                        {/* Tabla de documentos aplicados */}
                        {documentosAplicados.length > 0 ? (
                          <>
                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                              <Table size="small" stickyHeader>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Número</TableCell>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell align="right">Total</TableCell>
                                    <TableCell align="right">Aplicar</TableCell>
                                    <TableCell width={50}></TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {documentosAplicados.map((doc, idx) => (
                                    <TableRow key={idx} hover>
                                      <TableCell>
                                        <Chip 
                                          label={['', 'FV', 'FC', 'RV', 'RC', 'RO'][doc.tipoDocumentoReferencia] || 'DOC'} 
                                          size="small" 
                                          color="primary"
                                          variant="outlined"
                                        />
                                      </TableCell>
                                      <TableCell>{doc.numeroDocumento}</TableCell>
                                      <TableCell>
                                        {doc.fechaDocumento ? new Date(doc.fechaDocumento).toLocaleDateString() : '-'}
                                      </TableCell>
                                      <TableCell align="right">
                                        L.{formatCurrency(doc.montoDocumento)}
                                      </TableCell>
                                      <TableCell align="right">
                                        <TextField
                                          size="small"
                                          type="number"
                                          value={Number(doc.montoAplicado).toFixed(2)}
                                          onChange={(e) => editarMontoAplicado(doc.idDocumentoReferencia, parseFloat(e.target.value) || 0)}
                                          inputProps={{
                                            min: 0.01,
                                            max: Math.round((doc.montoDocumento || 0) * 100) / 100,
                                            step: 0.01
                                          }}
                                          sx={{ width: 100 }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => eliminarDocumento(doc.idDocumentoReferencia)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                            
                            <Box sx={{ 
                              mt: 2, 
                              p: 2, 
                              bgcolor: validarTotalAplicado() ? '#f6ffed' : '#fff2e8',
                              border: `1px solid ${validarTotalAplicado() ? '#b7eb8f' : '#ffbb96'}`,
                              borderRadius: 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {validarTotalAplicado() ? (
                                  <CheckCircleIcon sx={{ color: '#52c41a' }} />
                                ) : (
                                  <WarningIcon sx={{ color: '#fa8c16' }} />
                                )}
                                <Box>
                                  <Typography variant="body2">
                                    <strong>Total Aplicado:</strong> L.{formatCurrency(calcularTotalAplicado())}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>{form.modo === 'cargo' ? 'Cargo' : 'Abono'}:</strong> L.{formatCurrency(form.monto)}
                                  </Typography>
                                </Box>
                              </Box>
                              {!validarTotalAplicado() && (
                                <Typography variant="caption" color="warning.main">
                                  Los montos no coinciden
                                </Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            No hay documentos aplicados. Haga clic en "Buscar Documentos" para agregar.
                          </Alert>
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={resetForm}
                            disabled={loading}
                          >
                            Limpiar
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                            disabled={loading || !tienePermiso('Editar Auxiliar')}
                            color={form.modo === 'cargo' ? 'error' : 'success'}
                          >
                            {loading ? 'Guardando...' : `Registrar ${form.modo === 'cargo' ? 'Cargo' : 'Abono'}`}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Historial de Movimientos */}
          {auxiliarInfo && (
            <Grid item xs={12} lg={7}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">📄 Historial de Movimientos</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={exportarAExcel}
                      disabled={loadingInfo || !movimientosAuxiliar.length}
                    >
                      Exportar Excel
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={buscarAuxiliar}
                      disabled={loadingInfo}
                    >
                      Recargar
                    </Button>
                  </Box>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell sx={{ width: 30 }}></TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Tipo Doc</TableCell>
                        <TableCell align="right">Cargo</TableCell>
                        <TableCell align="right">Abono</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                        <TableCell>Partida</TableCell>
                        <TableCell align="center">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingInfo ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                            <CircularProgress size={30} />
                          </TableCell>
                        </TableRow>
                      ) : movimientosAuxiliar.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                            Sin movimientos registrados
                          </TableCell>
                        </TableRow>
                      ) : (
                        movimientosAuxiliar.map((mov, idx) => {
                          const tieneDetalles = mov.documentosAplicados && 
                            ((mov.documentosAplicados.$values || mov.documentosAplicados).length > 0);
                          const detalles = tieneDetalles 
                            ? (mov.documentosAplicados.$values || mov.documentosAplicados) 
                            : [];
                          const estaExpandido = filasExpandidas[mov.id] || false;

                          return (
                            <React.Fragment key={mov.id || idx}>
                              <TableRow hover sx={tieneDetalles ? { cursor: 'pointer' } : {}}>
                                <TableCell sx={{ p: 0, width: 30 }}>
                                  {tieneDetalles && (
                                    <IconButton
                                      size="small"
                                      onClick={() => setFilasExpandidas(prev => ({
                                        ...prev,
                                        [mov.id]: !prev[mov.id]
                                      }))}
                                    >
                                      {estaExpandido ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                    </IconButton>
                                  )}
                                </TableCell>
                                <TableCell>{idx + 1}</TableCell>
                                <TableCell>{mov.fechaDocumento || mov.fechaIngreso || ''}</TableCell>
                                <TableCell>
                                  {mov.tipoDocumento || ''}
                                  {tieneDetalles && (
                                    <Chip label={detalles.length} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} color="info" />
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'error.main', fontWeight: mov.cargo > 0 ? 'bold' : 'normal' }}>
                                  {mov.cargo > 0 ? `L. ${formatCurrency(mov.cargo)}` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'success.main', fontWeight: mov.abono > 0 ? 'bold' : 'normal' }}>
                                  {mov.abono > 0 ? `L. ${formatCurrency(mov.abono)}` : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  <Chip
                                    label={`L. ${formatCurrency(mov.saldo)}`}
                                    color={mov.saldo === 0 ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  {mov.numeroPartida ? (
                                    <Chip 
                                      label={`#${mov.numeroPartida}`} 
                                      size="small" 
                                      variant="outlined"
                                      icon={<PrintIcon fontSize="small" />}
                                      onClick={() => imprimirPartida(mov.idPartidaContable)}
                                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
                                    />
                                  ) : '-'}
                                </TableCell>
                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                  <Tooltip title={`Cambiar a ${(mov.cargo || 0) > 0 ? 'Abono' : 'Cargo'}`}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleCambiarTipo(mov)}
                                      disabled={loading}
                                    >
                                      <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Eliminar movimiento">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleEliminarMovimiento(mov)}
                                      disabled={loading}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              {tieneDetalles && (
                                <TableRow>
                                  <TableCell colSpan={9} sx={{ p: 0, borderBottom: estaExpandido ? undefined : 'none' }}>
                                    <Collapse in={estaExpandido} timeout="auto" unmountOnExit>
                                      <Box sx={{ m: 1, ml: 4, mr: 2, mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                          <DescriptionIcon fontSize="small" color="primary" />
                                          Documentos Aplicados ({detalles.length})
                                        </Typography>
                                        <Table size="small" sx={{ bgcolor: '#fafafa', borderRadius: 1 }}>
                                          <TableHead>
                                            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Tipo</TableCell>
                                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Número Documento</TableCell>
                                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Fecha</TableCell>
                                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Monto Doc.</TableCell>
                                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Monto Aplicado</TableCell>
                                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Saldo Pendiente</TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {detalles.map((det, dIdx) => (
                                              <TableRow key={det.idDetalle || dIdx}>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  <Chip 
                                                    label={['', 'FV', 'FC', 'RV', 'RC', 'RO'][det.tipoDocumentoReferencia] || 'DOC'} 
                                                    size="small" 
                                                    color="primary" 
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                  />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>{det.numeroDocumento || '-'}</TableCell>
                                                <TableCell sx={{ fontSize: '0.75rem' }}>
                                                  {det.fechaDocumento ? new Date(det.fechaDocumento).toLocaleDateString('es-HN') : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                                  L. {formatCurrency(det.montoDocumento)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1565c0' }}>
                                                  L. {formatCurrency(det.montoAplicado)}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontSize: '0.75rem', color: det.saldoPendiente > 0 ? 'warning.main' : 'success.main' }}>
                                                  L. {formatCurrency(det.saldoPendiente || 0)}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                            <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                                              <TableCell colSpan={3} sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Total</TableCell>
                                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                L. {formatCurrency(detalles.reduce((s, d) => s + (d.montoDocumento || 0), 0))}
                                              </TableCell>
                                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1565c0' }}>
                                                L. {formatCurrency(detalles.reduce((s, d) => s + (d.montoAplicado || 0), 0))}
                                              </TableCell>
                                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                L. {formatCurrency(detalles.reduce((s, d) => s + (d.saldoPendiente || 0), 0))}
                                              </TableCell>
                                            </TableRow>
                                          </TableBody>
                                        </Table>
                                      </Box>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {movimientosAuxiliar.length > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">📊 Total Cargos:</Typography>
                        <Typography variant="h6" sx={{ color: 'error.main' }}>
                          L. {formatCurrency(totales.cargos)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">📊 Total Abonos:</Typography>
                        <Typography variant="h6" sx={{ color: 'success.main' }}>
                          L. {formatCurrency(totales.abonos)}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">💰 Saldo Final:</Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: totales.saldo === 0 ? 'success.main' : 'primary.main',
                            fontWeight: 'bold'
                          }}
                        >
                          L. {formatCurrency(totales.saldo)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Grid>
          )}

          {/* Mensaje inicial cuando no hay búsqueda */}
          {!auxiliarInfo && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: '#f9f9f9' }}>
                <SearchIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                  Seleccione un tipo de auxiliar e ingrese la referencia para comenzar
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Los movimientos auxiliares generarán automáticamente las partidas contables
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Modal para visualizar PDF */}
        <Modal open={openModalPdf} onClose={handleCloseModalPdf}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              height: '90vh',
              bgcolor: 'white',
              boxShadow: 24,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header del modal */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                borderBottom: '1px solid #ddd',
                bgcolor: '#f5f5f5'
              }}
            >
              <Typography variant="h6" component="h2">
                {pdfNombre}
              </Typography>
              <Box>
                <IconButton
                  onClick={handleDownloadPdf}
                  color="primary"
                  title="Descargar PDF"
                  sx={{ mr: 1 }}
                >
                  <DownloadIcon />
                </IconButton>
                <IconButton
                  onClick={handleCloseModalPdf}
                  color="error"
                  title="Cerrar"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Contenedor del PDF */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                bgcolor: '#525659'
              }}
            >
              {pdfUrl && (
                <iframe
                  src={pdfUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none'
                  }}
                  title="Visor de PDF"
                />
              )}
            </Box>

            {/* Footer del modal */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                p: 2,
                borderTop: '1px solid #ddd',
                bgcolor: '#f5f5f5'
              }}
            >
              <Button
                variant="contained"
                color="error"
                onClick={handleCloseModalPdf}
                startIcon={<CloseIcon />}
              >
                Cerrar
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Modal para buscar documentos pendientes */}
        <Modal open={modalDocumentosOpen} onClose={() => setModalDocumentosOpen(false)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90%',
              maxWidth: 1200,
              height: '85vh',
              bgcolor: 'white',
              boxShadow: 24,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: '1px solid #ddd', bgcolor: '#f5f5f5' }}>
              <Typography variant="h6">🔍 Buscar Documentos Pendientes</Typography>
            </Box>

            {/* Filtros */}
            <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Documento *</InputLabel>
                    <Select
                      value={tipoDocumentoSeleccionado}
                      onChange={(e) => setTipoDocumentoSeleccionado(e.target.value)}
                      label="Tipo de Documento *"
                    >
                      {tiposDocumentoModal.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Desde"
                    InputLabelProps={{ shrink: true }}
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Hasta"
                    InputLabelProps={{ shrink: true }}
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={buscarDocumentosPendientes}
                    disabled={!tipoDocumentoSeleccionado || loadingDocumentos}
                    startIcon={loadingDocumentos ? <CircularProgress size={20} /> : <SearchIcon />}
                  >
                    {loadingDocumentos ? 'Buscando...' : 'Buscar'}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Tabla de documentos */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {loadingDocumentos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : documentosPendientes.length > 0 ? (
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={documentosSeleccionados.length > 0 && documentosSeleccionados.length < documentosPendientes.filter(d => d.saldoPendiente > 0).length}
                            checked={documentosPendientes.filter(d => d.saldoPendiente > 0).length > 0 && documentosSeleccionados.length === documentosPendientes.filter(d => d.saldoPendiente > 0).length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setDocumentosSeleccionados(documentosPendientes.filter(d => d.saldoPendiente > 0).map(d => d.idDocumento));
                              } else {
                                setDocumentosSeleccionados([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Número</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell>Cliente/Proveedor</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="right">Aplicado</TableCell>
                        <TableCell align="right">Saldo</TableCell>
                        <TableCell align="right">Aplicar</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documentosPendientes.map((doc) => {
                        const aplicado100 = doc.saldoPendiente <= 0;
                        return (
                        <TableRow 
                          key={doc.idDocumento} 
                          hover={!aplicado100}
                          selected={documentosSeleccionados.includes(doc.idDocumento)}
                          sx={aplicado100 ? { opacity: 0.5, bgcolor: '#f9f9f9' } : {}}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={documentosSeleccionados.includes(doc.idDocumento)}
                              disabled={aplicado100}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setDocumentosSeleccionados([...documentosSeleccionados, doc.idDocumento]);
                                } else {
                                  setDocumentosSeleccionados(documentosSeleccionados.filter(id => id !== doc.idDocumento));
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip label={doc.nombreTipoDocumento} size="small" />
                          </TableCell>
                          <TableCell>{doc.numeroDocumento}</TableCell>
                          <TableCell>
                            {doc.fechaDocumento ? new Date(doc.fechaDocumento).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{doc.cliente || '-'}</TableCell>
                          <TableCell align="right">L.{formatCurrency(doc.montoTotal)}</TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`L.${formatCurrency(doc.montoAplicado || 0)}`}
                              size="small"
                              color={aplicado100 ? 'error' : (doc.montoAplicado > 0 ? 'warning' : 'default')}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={`L.${formatCurrency(doc.saldoPendiente)}`}
                              size="small"
                              color={aplicado100 ? 'error' : 'success'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small"
                              type="number"
                              value={Number(doc.montoAplicar).toFixed(2)}
                              onChange={(e) => actualizarMontoAplicar(doc.idDocumento, parseFloat(e.target.value) || 0)}
                              inputProps={{
                                min: 0.01,
                                max: Math.round((doc.saldoPendiente || 0) * 100) / 100,
                                step: 0.01
                              }}
                              sx={{ width: 100 }}
                              disabled={aplicado100 || !documentosSeleccionados.includes(doc.idDocumento)}
                            />
                          </TableCell>
                        </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="body1" color="textSecondary">
                    {tipoDocumentoSeleccionado 
                      ? 'No hay documentos pendientes con los filtros seleccionados' 
                      : 'Seleccione un tipo de documento y haga clic en Buscar'}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ p: 2, borderTop: '1px solid #ddd', bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2">
                  <strong>Documentos Seleccionados:</strong> {documentosSeleccionados.length}
                </Typography>
                {documentosSeleccionados.length > 0 && (
                  <Typography variant="body2" color="primary">
                    <strong>Total:</strong> L.{formatCurrency(calcularTotalSeleccionado())}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setModalDocumentosOpen(false);
                    setDocumentosSeleccionados([]);
                    setDocumentosPendientes([]);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={aplicarDocumentosSeleccionados}
                  disabled={documentosSeleccionados.length === 0}
                  startIcon={<AddIcon />}
                >
                  Aplicar {documentosSeleccionados.length > 0 ? `(${documentosSeleccionados.length})` : ''}
                </Button>
              </Box>
            </Box>
          </Box>
        </Modal>
      </div>
    </div>
  );
}

export default GestionAuxiliares;
